import { InjectionToken } from '@angular/core';

import type { IAiPanelWidthMemoryProvider } from '../services/providers/i-ai-panel-width-memory.provider';
import type { ISessionAliasHandler } from '../services/handlers/i-session-alias.handler';
import type { ISessionAliasProvider } from '../services/providers/i-session-alias.provider';
import type { IPreviousSessionDigestHandler } from '../services/handlers/i-previous-session-digest.handler';
import type { IPreviousSessionDigestProvider } from '../services/providers/i-previous-session-digest.provider';
import type { IFlowChangeProvider } from '../services/providers/i-flow-change.provider';
import type { ISessionTransferProvider } from '../services/providers/i-session-transfer.provider';
import type { ISessionTransferHandler } from '../services/handlers/i-session-transfer.handler';

import type { IChatHistoryProvider } from '../services/providers/i-chat-history.provider';
import type { IChatProvider } from '../services/providers/i-chat.provider';
import type { IOutboundMessageProvider } from '../services/providers/i-outbound-message.provider';
import type { IReadReceiptProvider } from '../services/providers/i-read-receipt.provider';
import type { IReadReceiptHistoryProvider } from '../services/providers/i-read-receipt-history.provider';
import type { IPageVisibilityProvider } from '../services/providers/i-page-visibility.provider';
import type { ITypingProvider } from '../services/providers/i-typing.provider';
import type { ITypingHandler } from '../services/handlers/i-typing.handler';
import type { IChatHandler } from '../services/handlers/i-chat.handler';
import type { IOperatorConversationHandler } from '../services/handlers/i-operator-conversation.handler';
import type { ISessionRatingHandler } from '../services/handlers/i-session-rating.handler';
import type { ISessionRatingProvider } from '../services/providers/i-session-rating.provider';
import type { ISessionSummaryProvider } from '../services/providers/i-session-summary.provider';
import type { ITranslationStateHandler } from '../services/handlers/i-translation-state.handler';
import type { IOperatorPresenceProvider } from '../services/providers/i-operator-presence.provider';
import type { IOperatorSessionProvider } from '../services/providers/i-operator-session.provider';
import type { IMessageDeliveryStateHandler } from '../services/handlers/i-message-delivery-state.handler';
import type { IAttachmentHandler } from '../services/handlers/i-attachment.handler';
import type { ISessionAssignmentHandler } from '../services/handlers/i-session-assignment.handler';
import type { ISessionAssignmentSnapshotProvider } from '../services/providers/i-session-assignment-snapshot.provider';
import type { ISessionAssignmentProvider } from '../services/providers/i-session-assignment.provider';
import type { ISessionTakeoverProvider } from '../services/providers/i-session-takeover.provider';
import type { IAttachmentProvider } from '../services/providers/i-attachment.provider';
import type { ITranslationRequestProvider } from '../services/providers/i-translation-request.provider';
import type { ITranslationStreamProvider } from '../services/providers/i-translation-stream.provider';
import type { IClipboardProvider } from '../services/providers/i-clipboard.provider';
import type { IRichTextProvider } from '../services/providers/i-rich-text.provider';
import type { IMessageCompositionHandler } from '../services/handlers/i-message-composition.handler';
import type { ISynthesisProvider } from '../services/providers/i-synthesis.provider';
import type { IAudioPlaybackProvider } from '../services/providers/i-audio-playback.provider';
import type { IVoiceReadingHandler } from '../services/handlers/i-voice-reading.handler';
import type { IEmailVerificationHandler } from '../services/handlers/i-email-verification.handler';
import type { ISessionReminderHandler } from '../services/handlers/i-session-reminder.handler';
import type { IEmailVerificationProvider } from '../services/providers/i-email-verification.provider';
import type { ISessionReminderProvider } from '../services/providers/i-session-reminder.provider';
import type { IReminderExchangeProvider } from '../services/providers/i-reminder-exchange.provider';
import type { ISessionStatusProvider } from '../services/providers/i-session-status.provider';
import type { ISessionLifecycleHandler } from '../services/handlers/i-session-lifecycle.handler';
import type { IPreTriageHandler } from '../services/handlers/i-pre-triage.handler';
import type { ITriageTicketSnapshotProvider } from '../services/providers/i-triage-ticket-snapshot.provider';
import type { ITriageTicketProvider } from '../services/providers/i-triage-ticket.provider';
import type { ITriageDigestHandler } from '../services/handlers/i-triage-digest.handler';
import type { ITriageDigestProvider } from '../services/providers/i-triage-digest.provider';
import type { ITakeoverResumptionHandler } from '../services/handlers/i-takeover-resumption.handler';
import type { ITakeoverResumptionProvider } from '../services/providers/i-takeover-resumption.provider';
import type { ISessionSummaryHandler } from '../services/handlers/i-session-summary.handler';
import type { IOperatorHandoffHandler } from '../services/handlers/i-operator-handoff.handler';

export const TRANSLATION_STREAM_PROVIDER = new InjectionToken<ITranslationStreamProvider>(
  'ITranslationStreamProvider',
);
export const TRANSLATION_REQUEST_PROVIDER = new InjectionToken<ITranslationRequestProvider>(
  'ITranslationRequestProvider',
);
export const TRANSLATION_STATE_HANDLER = new InjectionToken<ITranslationStateHandler>(
  'ITranslationStateHandler',
);
export const SYNTHESIS_PROVIDER = new InjectionToken<ISynthesisProvider>('ISynthesisProvider');
export const AUDIO_PLAYBACK_PROVIDER = new InjectionToken<IAudioPlaybackProvider>(
  'IAudioPlaybackProvider',
);
export const VOICE_READING_HANDLER = new InjectionToken<IVoiceReadingHandler>(
  'IVoiceReadingHandler',
);
export const AI_PANEL_WIDTH_MEMORY_PROVIDER = new InjectionToken<IAiPanelWidthMemoryProvider>(
  'IAiPanelWidthMemoryProvider',
);
export const CHAT_PROVIDER = new InjectionToken<IChatProvider>('IChatProvider');
export const OUTBOUND_MESSAGE_PROVIDER = new InjectionToken<IOutboundMessageProvider>(
  'IOutboundMessageProvider',
);
export const CHAT_HISTORY_PROVIDER = new InjectionToken<IChatHistoryProvider>(
  'IChatHistoryProvider',
);
export const CHAT_HANDLER = new InjectionToken<IChatHandler>('IChatHandler');
export const OPERATOR_SESSION_PROVIDER = new InjectionToken<IOperatorSessionProvider>(
  'IOperatorSessionProvider',
);
export const OPERATOR_PRESENCE_PROVIDER = new InjectionToken<IOperatorPresenceProvider>(
  'IOperatorPresenceProvider',
);
export const OPERATOR_CONVERSATION_HANDLER = new InjectionToken<IOperatorConversationHandler>(
  'IOperatorConversationHandler',
);
export const READ_RECEIPT_PROVIDER = new InjectionToken<IReadReceiptProvider>(
  'IReadReceiptProvider',
);
export const READ_RECEIPT_HISTORY_PROVIDER = new InjectionToken<IReadReceiptHistoryProvider>(
  'IReadReceiptHistoryProvider',
);
export const PAGE_VISIBILITY_PROVIDER = new InjectionToken<IPageVisibilityProvider>(
  'IPageVisibilityProvider',
);
export const MESSAGE_DELIVERY_STATE_HANDLER = new InjectionToken<IMessageDeliveryStateHandler>(
  'IMessageDeliveryStateHandler',
);
export const TYPING_PROVIDER = new InjectionToken<ITypingProvider>('ITypingProvider');
export const TYPING_HANDLER = new InjectionToken<ITypingHandler>('ITypingHandler');
export const SESSION_ASSIGNMENT_PROVIDER = new InjectionToken<ISessionAssignmentProvider>(
  'ISessionAssignmentProvider',
);
export const SESSION_ASSIGNMENT_SNAPSHOT_PROVIDER =
  new InjectionToken<ISessionAssignmentSnapshotProvider>('ISessionAssignmentSnapshotProvider');
export const SESSION_TAKEOVER_PROVIDER = new InjectionToken<ISessionTakeoverProvider>(
  'ISessionTakeoverProvider',
);
export const SESSION_ASSIGNMENT_HANDLER = new InjectionToken<ISessionAssignmentHandler>(
  'ISessionAssignmentHandler',
);
export const ATTACHMENT_PROVIDER = new InjectionToken<IAttachmentProvider>('IAttachmentProvider');
export const ATTACHMENT_HANDLER = new InjectionToken<IAttachmentHandler>('IAttachmentHandler');
export const SESSION_RATING_PROVIDER = new InjectionToken<ISessionRatingProvider>(
  'ISessionRatingProvider',
);
export const SESSION_RATING_HANDLER = new InjectionToken<ISessionRatingHandler>(
  'ISessionRatingHandler',
);
export const CLIPBOARD_PROVIDER = new InjectionToken<IClipboardProvider>('IClipboardProvider');
export const RICH_TEXT_PROVIDER = new InjectionToken<IRichTextProvider>('IRichTextProvider');
export const MESSAGE_COMPOSITION_HANDLER = new InjectionToken<IMessageCompositionHandler>(
  'IMessageCompositionHandler',
);

export const SESSION_REMINDER_PROVIDER = new InjectionToken<ISessionReminderProvider>(
  'ISessionReminderProvider',
);
export const REMINDER_EXCHANGE_PROVIDER = new InjectionToken<IReminderExchangeProvider>(
  'IReminderExchangeProvider',
);
export const SESSION_REMINDER_HANDLER = new InjectionToken<ISessionReminderHandler>(
  'ISessionReminderHandler',
);

export const EMAIL_VERIFICATION_PROVIDER = new InjectionToken<IEmailVerificationProvider>(
  'IEmailVerificationProvider',
);
export const EMAIL_VERIFICATION_HANDLER = new InjectionToken<IEmailVerificationHandler>(
  'IEmailVerificationHandler',
);

export const SESSION_STATUS_PROVIDER = new InjectionToken<ISessionStatusProvider>(
  'ISessionStatusProvider',
);
export const SESSION_LIFECYCLE_HANDLER = new InjectionToken<ISessionLifecycleHandler>(
  'ISessionLifecycleHandler',
);
export const TRIAGE_TICKET_PROVIDER = new InjectionToken<ITriageTicketProvider>(
  'ITriageTicketProvider',
);
export const TRIAGE_TICKET_SNAPSHOT_PROVIDER = new InjectionToken<ITriageTicketSnapshotProvider>(
  'ITriageTicketSnapshotProvider',
);
export const PRE_TRIAGE_HANDLER = new InjectionToken<IPreTriageHandler>('IPreTriageHandler');
export const TRIAGE_DIGEST_PROVIDER = new InjectionToken<ITriageDigestProvider>(
  'ITriageDigestProvider',
);
export const TRIAGE_DIGEST_HANDLER = new InjectionToken<ITriageDigestHandler>(
  'ITriageDigestHandler',
);
export const TAKEOVER_RESUMPTION_PROVIDER = new InjectionToken<ITakeoverResumptionProvider>(
  'ITakeoverResumptionProvider',
);
export const TAKEOVER_RESUMPTION_HANDLER = new InjectionToken<ITakeoverResumptionHandler>(
  'ITakeoverResumptionHandler',
);
export const SESSION_SUMMARY_PROVIDER = new InjectionToken<ISessionSummaryProvider>(
  'ISessionSummaryProvider',
);
export const SESSION_ALIAS_PROVIDER = new InjectionToken<ISessionAliasProvider>(
  'ISessionAliasProvider',
);
export const SESSION_ALIAS_HANDLER = new InjectionToken<ISessionAliasHandler>(
  'ISessionAliasHandler',
);
export const PREVIOUS_SESSION_DIGEST_PROVIDER = new InjectionToken<IPreviousSessionDigestProvider>(
  'IPreviousSessionDigestProvider',
);
export const PREVIOUS_SESSION_DIGEST_HANDLER = new InjectionToken<IPreviousSessionDigestHandler>(
  'IPreviousSessionDigestHandler',
);
export const SESSION_SUMMARY_HANDLER = new InjectionToken<ISessionSummaryHandler>(
  'ISessionSummaryHandler',
);
export const SESSION_TRANSFER_PROVIDER = new InjectionToken<ISessionTransferProvider>(
  'ISessionTransferProvider',
);
export const FLOW_CHANGE_PROVIDER = new InjectionToken<IFlowChangeProvider>('IFlowChangeProvider');
export const SESSION_TRANSFER_HANDLER = new InjectionToken<ISessionTransferHandler>(
  'ISessionTransferHandler',
);
export const OPERATOR_HANDOFF_HANDLER = new InjectionToken<IOperatorHandoffHandler>(
  'IOperatorHandoffHandler',
);
